import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './BusinessInfo.css';

const BusinessInfo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { businessData, searchQuery } = location.state || {};

  if (!businessData) {
    return (
      <div className="business-info-container">
        <div className="no-data">
          <h2>No business information available</h2>
          <button onClick={() => navigate('/')} className="back-button">
            Start New Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="business-info-container">
      <div className="business-info-content">
        <div className="info-header">
          <button onClick={() => navigate('/')} className="back-arrow">
            ← Back
          </button>
          <h1 className="business-title">{businessData.name || searchQuery?.businessName}</h1>
          <p className="business-location">{businessData.location || searchQuery?.zipCode}</p>
          
          {/* Data Source Indicators */}
          {businessData.sources && (
            <div className="data-sources">
              <span className="source-label">Data from:</span>
              {businessData.sources.googlePlaces && (
                <span className="source-badge google">
                  <span className="source-icon">🔍</span> Google Places
                </span>
              )}
              {businessData.sources.serper && (
                <span className="source-badge serper">
                  <span className="source-icon">🌐</span> Serper
                </span>
              )}
            </div>
          )}
        </div>

        <div className="info-grid">
          <div className="info-card">
            <div className="card-icon">📍</div>
            <div className="card-content">
              <h3>Address</h3>
              <p>{businessData.address || 'Not available'}</p>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">📞</div>
            <div className="card-content">
              <h3>Contact</h3>
              <p>Phone: {businessData.phone || 'Not available'}</p>
              {businessData.internationalPhone && businessData.internationalPhone !== businessData.phone && (
                <p>International: {businessData.internationalPhone}</p>
              )}
              <p>Email: {businessData.email || 'Not available'}</p>
              <p>Website: {businessData.website ? (
                <a href={businessData.website} target="_blank" rel="noopener noreferrer">
                  {businessData.website}
                </a>
              ) : 'Not available'}</p>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">🏢</div>
            <div className="card-content">
              <h3>Business Details</h3>
              <p>Type: {businessData.businessTypeDisplay || businessData.businessType || 'Not specified'}</p>
              {businessData.primaryType && (
                <p className="google-data">
                  <span className="data-source-mini">Google</span>
                  Category: {businessData.primaryType}
                </p>
              )}
              <p>Industry: {businessData.industry || 'Not specified'}</p>
              {businessData.businessStatus && (
                <p className="google-data">
                  <span className="data-source-mini">Google</span>
                  Status: <span className={`status-badge ${businessData.businessStatus === 'OPERATIONAL' ? 'operational' : 'closed'}`}>
                    {businessData.businessStatus}
                  </span>
                </p>
              )}
              {businessData.rating && (
                <p>Rating: ⭐ {businessData.rating.toFixed(1)} ({businessData.ratingCount || 0} reviews)</p>
              )}
              {businessData.priceLevel && (
                <p>Price Level: {'$'.repeat(businessData.priceLevel)}
                  {businessData.priceRange && <span className="price-range"> ({businessData.priceRange})</span>}
                </p>
              )}
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <h3>Company Size</h3>
              <p>Employees: {businessData.employees || 'Not specified'}</p>
              <p>Annual Revenue: {businessData.revenue || 'Not available'}</p>
            </div>
          </div>

          {businessData.description && (
            <div className="info-card full-width">
              <div className="card-icon">📝</div>
              <div className="card-content">
                <h3>Description</h3>
                <p>{businessData.description}</p>
              </div>
            </div>
          )}

          {businessData.hours && (
            <div className="info-card full-width">
              <div className="card-icon">🕐</div>
              <div className="card-content">
                <h3>Business Hours</h3>
                <div className="hours-list">
                  {Object.entries(businessData.hours).map(([day, hours]) => (
                    <div key={day} className="hours-row">
                      <span className="day">{day}:</span>
                      <span className="hours">{hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Services & Amenities Section */}
          {(businessData.delivery !== null || businessData.takeout !== null || businessData.dineIn !== null || 
            businessData.curbsidePickup !== null || businessData.reservable !== null) && (
            <div className="info-card">
              <div className="card-icon">🍽️</div>
              <div className="card-content">
                <h3>Services</h3>
                <div className="amenities-list">
                  {businessData.delivery && <span className="amenity-tag">✓ Delivery</span>}
                  {businessData.takeout && <span className="amenity-tag">✓ Takeout</span>}
                  {businessData.dineIn && <span className="amenity-tag">✓ Dine-in</span>}
                  {businessData.curbsidePickup && <span className="amenity-tag">✓ Curbside Pickup</span>}
                  {businessData.reservable && <span className="amenity-tag">✓ Reservations</span>}
                </div>
              </div>
            </div>
          )}

          {/* Food & Drinks Section */}
          {(businessData.servesBeer !== null || businessData.servesWine !== null || 
            businessData.servesCocktails !== null || businessData.servesCoffee !== null ||
            businessData.servesBreakfast !== null || businessData.servesLunch !== null ||
            businessData.servesDinner !== null || businessData.servesBrunch !== null) && (
            <div className="info-card">
              <div className="card-icon">🍷</div>
              <div className="card-content">
                <h3>Menu Offerings</h3>
                <div className="amenities-list">
                  {businessData.servesBreakfast && <span className="amenity-tag">🍳 Breakfast</span>}
                  {businessData.servesBrunch && <span className="amenity-tag">🥞 Brunch</span>}
                  {businessData.servesLunch && <span className="amenity-tag">🥗 Lunch</span>}
                  {businessData.servesDinner && <span className="amenity-tag">🍽️ Dinner</span>}
                  {businessData.servesDessert && <span className="amenity-tag">🍰 Dessert</span>}
                  {businessData.servesVegetarianFood && <span className="amenity-tag">🥬 Vegetarian</span>}
                  {businessData.servesCoffee && <span className="amenity-tag">☕ Coffee</span>}
                  {businessData.servesBeer && <span className="amenity-tag">🍺 Beer</span>}
                  {businessData.servesWine && <span className="amenity-tag">🍷 Wine</span>}
                  {businessData.servesCocktails && <span className="amenity-tag">🍸 Cocktails</span>}
                </div>
              </div>
            </div>
          )}

          {/* Amenities Section */}
          {(businessData.outdoorSeating !== null || businessData.liveMusic !== null || 
            businessData.goodForGroups !== null || businessData.goodForChildren !== null ||
            businessData.goodForWatchingSports !== null || businessData.allowsDogs !== null ||
            businessData.menuForChildren !== null || businessData.restroom !== null) && (
            <div className="info-card">
              <div className="card-icon">✨</div>
              <div className="card-content">
                <h3>Amenities</h3>
                <div className="amenities-list">
                  {businessData.outdoorSeating && <span className="amenity-tag">🌳 Outdoor Seating</span>}
                  {businessData.liveMusic && <span className="amenity-tag">🎵 Live Music</span>}
                  {businessData.goodForGroups && <span className="amenity-tag">👥 Good for Groups</span>}
                  {businessData.goodForChildren && <span className="amenity-tag">👶 Kid-friendly</span>}
                  {businessData.menuForChildren && <span className="amenity-tag">🧒 Kids Menu</span>}
                  {businessData.goodForWatchingSports && <span className="amenity-tag">📺 Sports TV</span>}
                  {businessData.allowsDogs && <span className="amenity-tag">🐕 Dog-friendly</span>}
                  {businessData.restroom && <span className="amenity-tag">🚻 Restroom</span>}
                </div>
              </div>
            </div>
          )}

          {/* Parking & Accessibility */}
          {(businessData.parkingOptions || businessData.accessibilityOptions || businessData.evChargeOptions) && (
            <div className="info-card">
              <div className="card-icon">🚗</div>
              <div className="card-content">
                <h3>Parking & Accessibility</h3>
                {businessData.parkingOptions && (
                  <div>
                    <p className="sub-heading">Parking:</p>
                    <div className="amenities-list">
                      {businessData.parkingOptions.paidParkingLot && <span className="amenity-tag">💵 Paid Lot</span>}
                      {businessData.parkingOptions.paidStreetParking && <span className="amenity-tag">💵 Paid Street</span>}
                      {businessData.parkingOptions.freeParkingLot && <span className="amenity-tag">🆓 Free Lot</span>}
                      {businessData.parkingOptions.freeStreetParking && <span className="amenity-tag">🆓 Free Street</span>}
                      {businessData.parkingOptions.valetParking && <span className="amenity-tag">🎩 Valet</span>}
                      {businessData.parkingOptions.freeGarageParking && <span className="amenity-tag">🏢 Free Garage</span>}
                      {businessData.parkingOptions.paidGarageParking && <span className="amenity-tag">💵 Paid Garage</span>}
                    </div>
                  </div>
                )}
                {businessData.accessibilityOptions && (
                  <div>
                    <p className="sub-heading">Accessibility:</p>
                    <div className="amenities-list">
                      {businessData.accessibilityOptions.wheelchairAccessibleEntrance && <span className="amenity-tag">♿ Wheelchair Entrance</span>}
                      {businessData.accessibilityOptions.wheelchairAccessibleParking && <span className="amenity-tag">♿ Wheelchair Parking</span>}
                      {businessData.accessibilityOptions.wheelchairAccessibleRestroom && <span className="amenity-tag">♿ Wheelchair Restroom</span>}
                      {businessData.accessibilityOptions.wheelchairAccessibleSeating && <span className="amenity-tag">♿ Wheelchair Seating</span>}
                    </div>
                  </div>
                )}
                {businessData.evChargeOptions && (
                  <div>
                    <p className="sub-heading">EV Charging:</p>
                    <div className="amenities-list">
                      {businessData.evChargeOptions.numberOfConnectors && 
                        <span className="amenity-tag">⚡ {businessData.evChargeOptions.numberOfConnectors} Connectors</span>}
                      {businessData.evChargeOptions.connectorTypes?.map((type, idx) => (
                        <span key={idx} className="amenity-tag">🔌 {type}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Options */}
          {businessData.paymentOptions && (
            <div className="info-card">
              <div className="card-icon">💳</div>
              <div className="card-content">
                <h3>Payment Options</h3>
                <div className="amenities-list">
                  {businessData.paymentOptions.acceptsCreditCards && <span className="amenity-tag">💳 Credit Cards</span>}
                  {businessData.paymentOptions.acceptsDebitCards && <span className="amenity-tag">💳 Debit Cards</span>}
                  {businessData.paymentOptions.acceptsCashOnly && <span className="amenity-tag">💵 Cash Only</span>}
                  {businessData.paymentOptions.acceptsNfc && <span className="amenity-tag">📱 NFC/Mobile Pay</span>}
                </div>
              </div>
            </div>
          )}

          {/* Google Places Exclusive Data */}
          {businessData.sources?.googlePlaces && (
            <div className="info-card full-width google-highlight">
              <div className="google-badge-corner">
                <span>Google Places Data</span>
              </div>
              <div className="card-icon">🔍</div>
              <div className="card-content">
                <h3>Additional Information from Google</h3>
                <div className="google-details-grid">
                  {businessData.latitude && businessData.longitude && (
                    <div className="detail-item">
                      <strong>📌 Coordinates:</strong>
                      <span>{businessData.latitude.toFixed(6)}, {businessData.longitude.toFixed(6)}</span>
                    </div>
                  )}
                  {businessData.businessStatus && (
                    <div className="detail-item">
                      <strong>🏪 Business Status:</strong>
                      <span className={businessData.businessStatus === 'OPERATIONAL' ? 'text-green' : 'text-red'}>
                        {businessData.businessStatus}
                      </span>
                    </div>
                  )}
                  {businessData.primaryType && (
                    <div className="detail-item">
                      <strong>🏷️ Primary Category:</strong>
                      <span>{businessData.primaryType.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {businessData.priceRange && (
                    <div className="detail-item">
                      <strong>💰 Price Range:</strong>
                      <span>{businessData.priceRange}</span>
                    </div>
                  )}
                  {businessData.userRatingCount > 0 && (
                    <div className="detail-item">
                      <strong>⭐ Google Reviews:</strong>
                      <span>{businessData.rating?.toFixed(1)} stars ({businessData.userRatingCount} reviews)</span>
                    </div>
                  )}
                  {businessData.internationalPhone && (
                    <div className="detail-item">
                      <strong>☎️ International Phone:</strong>
                      <span>{businessData.internationalPhone}</span>
                    </div>
                  )}
                </div>
                
                {/* Google Places Verified Amenities */}
                {(businessData.delivery !== null || businessData.takeout !== null || 
                  businessData.dineIn !== null || businessData.reservable !== null ||
                  businessData.outdoorSeating !== null || businessData.restroom !== null) && (
                  <div className="google-verified-section">
                    <h4 className="verified-label">✅ Google Verified Features</h4>
                    <div className="verified-features">
                      {businessData.delivery === true && <span className="verified-tag">✓ Delivery Available</span>}
                      {businessData.delivery === false && <span className="verified-tag unavailable">✗ No Delivery</span>}
                      {businessData.takeout === true && <span className="verified-tag">✓ Takeout Available</span>}
                      {businessData.takeout === false && <span className="verified-tag unavailable">✗ No Takeout</span>}
                      {businessData.dineIn === true && <span className="verified-tag">✓ Dine-in Available</span>}
                      {businessData.dineIn === false && <span className="verified-tag unavailable">✗ No Dine-in</span>}
                      {businessData.reservable === true && <span className="verified-tag">✓ Accepts Reservations</span>}
                      {businessData.reservable === false && <span className="verified-tag unavailable">✗ No Reservations</span>}
                      {businessData.outdoorSeating === true && <span className="verified-tag">✓ Outdoor Seating</span>}
                      {businessData.outdoorSeating === false && <span className="verified-tag unavailable">✗ No Outdoor Seating</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button onClick={() => navigate('/')} className="primary-button">
            Search Another Business
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfo;